import { useState, useEffect, useCallback } from 'react'
import localforage from 'localforage'

type UseLocalForageKeyDirectOptions = {
  /**
   * If true, defers the initial cache load to requestIdleCallback.
   * Useful when the data is not immediately needed (e.g., Apollo already has data).
   */
  deferLoad?: boolean
  /**
   * If true, skips loading from IndexedDB entirely.
   * Returns immediately with loading: false and data: undefined.
   */
  skip?: boolean
}

/**
 * A hook to use a key from localforage (IndexedDB) with direct state management.
 *
 * Unlike useLocalForageKey (which uses React Query), this hook:
 * - Updates state immediately on setData (optimistic update)
 * - Writes to IndexedDB asynchronously (fire and forget)
 * - Does not share state across components (each instance has its own state)
 *
 * Best for: SWR caching where immediate UI updates are desired and
 * cross-component synchronization is not needed.
 *
 * @param key - The key to read/write in localforage
 * @param options - Optional configuration
 */
export const useLocalForageKeyDirect = <T extends {}>(
  key: string,
  options?: UseLocalForageKeyDirectOptions
) => {
  const [data, setDataState] = useState<T | undefined>(undefined)
  const [loading, setLoading] = useState(true)

  const skip = options?.skip ?? false

  // Read from IndexedDB on mount (or when key changes)
  useEffect(() => {
    // Skip loading entirely if skip option is enabled
    if (skip) {
      setDataState(undefined)
      setLoading(false)
      return
    }

    let cancelled = false
    let idleHandle: number | undefined

    // deferLoad is intentionally not in deps - we only want to check it at mount/key-change,
    // not re-trigger loading if it changes mid-flight (e.g., Apollo returns data)
    const shouldDefer = options?.deferLoad ?? false

    const loadData = () => {
      localforage.getItem<T>(key).then(value => {
        if (!cancelled) {
          setDataState(value ?? undefined)
          setLoading(false)
        }
      })
    }

    if (shouldDefer && typeof requestIdleCallback !== 'undefined') {
      // Defer loading to browser idle time for housekeeping operations
      idleHandle = requestIdleCallback(loadData)
    } else {
      // Load immediately - some browsers like Safari do not support requestIdleCallback
      loadData()
    }

    return () => {
      cancelled = true
      if (idleHandle !== undefined) {
        cancelIdleCallback(idleHandle)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, skip])

  // Write to IndexedDB and update state
  const setData = useCallback(
    (value: T) => {
      setDataState(value)
      // Fire and forget - no need to wait, but handle quota errors gracefully
      localforage.setItem(key, value).catch(err => {
        if (err?.name === 'QuotaExceededError') {
          console.warn('[IndexedDB] Storage quota exceeded, cache write failed for key:', key)
        } else {
          console.error('[IndexedDB] Failed to write cache:', err)
        }
      })
    },
    [key]
  )

  return {
    loading,
    data,
    setData,
    hasKey: !loading ? Boolean(data) : undefined,
  }
}
